<template>
  <div>
    <div class="mt-3">
      <div class="row">

        <!-- LEFT (40%) -->
        <div class="col-lg-5">

          <!-- GENERATE REPORT -->
          <div class="panel">
            <header class="panel__header">
              <div class="profile-compact">
                <div class="logo-container">
                  <font-awesome-icon icon="file" class="icon-svg--sm"/>
                </div>
                <div class="profile-compact__info">
                  <div class="profile-compact__top">
                    <div class="profile-compact__text">
                      {{ $t('maintenance.reports.generate_new') }}
                      <span class="section-compact__secondary">
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="profile-compact__actions">
                <span class="icon-btn"
                      @click="generateReport"
                      data-qid="reporting-generate_report-button"
                      v-b-tooltip.hover
                      :title="$t('maintenance.reports.generate_new')"
                      v-if="canEditReport"
                >
                  <font-awesome-icon icon="file-word"/>
                </span>
              </div>
            </header>

            <div class="mt-2 pl-3" v-if="activity.task_count !== 0 && activity.task_count !== undefined && activity.progress !== 100 && activity.status !== 'completed'">
              <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
              {{$t('tasks.messages.unresolved_tasks')}}
            </div>
            <div class="mt-2 pl-3" v-if="measureTask && measureTask.id && measureTask.data_status !== 'completed'">
              <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
              {{$t('tasks.messages.measure_unresolved_tasks')}}
            </div>
            <div class="mt-2 pl-3" v-if="activity.system_risk_count !== 0 && activity.system_risk_count !== undefined">
              <font-awesome-icon icon="exclamation-triangle" class="risk-6"/>
              {{$t('tasks.messages.unresolved_risk_assessments')}}
            </div>

            <div class="panel__body">
              <div class="card" data-qid="basic-info-section">
                <div class="">
                  <div class="content-view-body">
                    <b-alert v-if="reportError.length"
                             show
                             variant="danger"
                             class="mt-4"
                             qid="attachments-tp-error"
                    >
                      {{ reportError }}
                    </b-alert>
                    <b-alert v-if="error.length"
                             show
                             variant="danger"
                             qid="attachments-tp-error"
                    >
                      {{ error }}
                    </b-alert>

                    <!-- ATTACHMENTS: laid out like the attachments on a task -->
                    <section class="v2-field v2-field--flush report-files" data-qid="reporting-attachments" @dragover.prevent @drop.prevent="dropReportFiles">
                      <header class="v2-field__head">
                        <h4>{{ $t('attachments.label') }}</h4>
                        <span class="v2-field__meta">{{ getMedia.length }}</span>
                        <file-upload v-if="canEditReport"
                                     ref="report-uploader"
                                     v-model="reportFiles"
                                     input-id="report-upload"
                                     :multiple="true"
                                     @input="uploadReportFiles"
                                     class="btn btn-secondary btn-sm"
                                     data-qid="reporting-upload-document"
                        >
                          <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('tasks.modal.add_attachment_label') }}
                        </file-upload>
                      </header>
                      <div v-for="file in getMedia" v-bind:key="file.id" class="task-file" data-qid="report-file">
                        <font-awesome-icon icon="paperclip" class="task-file__icon"/>
                        <div class="task-file__text" v-if="renamingFile.id === file.id">
                          <b-form-input v-model="renamingFile.name"
                                        size="sm"
                                        @keyup.enter="saveFileName"
                                        @keyup.esc="cancelRename"
                                        data-qid="report-file-rename-input"
                          ></b-form-input>
                        </div>
                        <div class="task-file__text" v-else>
                          <a href="#" @click.prevent="openDocument(file)" data-qid="report-file-name">{{ file.file_name }}</a>
                          <div class="task-file__sub">{{ file.created_at | formatDate }}</div>
                        </div>
                        <template v-if="renamingFile.id === file.id">
                          <span class="v2-field__icon" @click="cancelRename" data-qid="report-file-rename-cancel">
                            <font-awesome-icon icon="rotate-left"/>
                          </span>
                          <span class="v2-field__icon" @click="saveFileName" data-qid="report-file-rename-save">
                            <font-awesome-icon icon="save"/>
                          </span>
                        </template>
                        <template v-else>
                          <span class="v2-field__icon"
                                v-if="canEditReport"
                                v-b-tooltip.hover
                                :title="$t('reports.rename_file')"
                                @click="startRename(file)"
                                data-qid="report-file-rename"
                          ><font-awesome-icon icon="pen"/></span>
                          <span class="v2-field__icon"
                                v-b-tooltip.hover
                                :title="$t('tasks.v2.download')"
                                @click="downloadMedia(file)"
                                data-qid="download-media"
                          ><font-awesome-icon icon="cloud-download-alt"/></span>
                          <span class="v2-field__icon v2-field__icon--danger"
                                v-if="canEditReport"
                                v-b-tooltip.hover
                                :title="$t('attachments.delete.title')"
                                @click="openDeleteAttachmentModal(file)"
                                data-qid="remove-media-item"
                          ><font-awesome-icon icon="trash-alt"/></span>
                        </template>
                      </div>
                      <div v-if="!getMedia.length" class="v2-field__empty task-file__empty">{{ $t('tasks.v2.no_attachments') }}</div>
                      <div class="report-files__hint">{{ $t('attachments.file_size_label') }}</div>
                    </section>

                    <!-- LINKS -->
                    <section class="v2-field v2-field--flush report-files" data-qid="reporting-links">
                      <header class="v2-field__head">
                        <h4>{{ $t('links.label') }}</h4>
                        <span class="v2-field__meta">{{ getLinks.length }}</span>
                        <b-button v-if="canEditReport && !showInput"
                                  size="sm"
                                  variant="secondary"
                                  @click="showInput = true"
                                  data-qid="add-another-link"
                        >
                          <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('reports.add_link') }}
                        </b-button>
                      </header>
                      <div v-for="link in getLinks" v-bind:key="link.id" class="task-file">
                        <font-awesome-icon icon="link" class="task-file__icon"/>
                        <div class="task-file__text">
                          <a :href="link.url" target="_blank" qid="create-tp-link-item">{{ link.name ? link.name : link.url }}</a>
                          <div class="task-file__sub text-truncate">
                            <template v-if="link.name">{{ link.url }} · </template>{{ link.created_at | formatDate }}
                          </div>
                        </div>
                        <a :href="link.url"
                           target="_blank"
                           class="v2-field__icon"
                           v-b-tooltip.hover
                           :title="$t('reports.open_link')"
                        ><font-awesome-icon icon="external-link-alt"/></a>
                        <span class="v2-field__icon v2-field__icon--danger"
                              v-if="canEditReport"
                              v-b-tooltip.hover
                              :title="$t('attachments.delete.title')"
                              @click="openDeleteLinkModal(link)"
                              qid="view-tp-remove-link"
                        ><font-awesome-icon icon="trash-alt"/></span>
                      </div>
                      <div v-if="!getLinks.length && !showInput" class="v2-field__empty task-file__empty">{{ $t('reports.no_links') }}</div>

                      <div v-if="showInput" class="report-files__form">
                        <label>{{ $t('system.link') }}</label>
                        <b-form-input
                            type="text"
                            aria-describedby="inputGroup"
                            v-model="inputLink"
                            :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                            qid="create-p-links"
                        ></b-form-input>
                        <label class="mt-2">{{ $t('table.name') }}</label>
                        <b-form-input
                            type="text"
                            aria-describedby="inputGroup"
                            v-model="inputLinkName"
                            :placeholder="inputLink ? inputLink : $t('table.name')"
                            qid="create-p-links"
                        ></b-form-input>
                        <div class="v2-field__actions">
                          <b-button size="sm" variant="secondary" @click="showInput = false" data-qid="activity-edit-button-cancel">{{ $t('tasks.modal.discard') }}</b-button>
                          <b-button size="sm" variant="success" id="inputGroup" @click="handleLink" :disabled="!inputLink" data-qid="activity-edit-button-submit">{{ $t('tasks.modal.save') }}</b-button>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <!-- EXPORT CENTER: attachments as ZIP and the task list as Excel -->
          <div class="panel" data-qid="reporting-export-center">
            <header class="panel__header">
              <div class="profile-compact">
                <div class="logo-container">
                  <font-awesome-icon icon="cloud-download-alt" class="icon-svg--sm"/>
                </div>
                <div class="profile-compact__info">
                  <div class="profile-compact__top">
                    <div class="profile-compact__text">
                      {{ $t('reports.export_center') }}
                      <span class="section-compact__secondary"></span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            <div class="panel__body">
              <!-- ZIP of all attachments, laid out like the attachments on a task -->
              <section class="v2-field v2-field--flush report-files" data-qid="reporting-export-zip">
                <header class="v2-field__head">
                  <h4>{{ $t('reports.export_attachments') }}</h4>
                  <b-button v-if="canExportAttachments"
                            size="sm"
                            variant="secondary"
                            @click="$refs.downloadAll.showDownloadModal()"
                            data-qid="reporting-export-zip-create"
                  >
                    <font-awesome-icon icon="file-download" class="mr-1"/>{{ $t('reports.create_zip') }}
                  </b-button>
                </header>
                <div class="report-files__intro">{{ $t('reports.export_attachments_hint') }}</div>
                <div v-if="exportFile" class="task-file" data-qid="reporting-export-file">
                  <font-awesome-icon icon="file-download" class="task-file__icon"/>
                  <div class="task-file__text">
                    <a href="#" @click.prevent="downloadMedia(exportFile)">{{ exportFile.file_name }}</a>
                    <div class="task-file__sub">{{ exportFile.created_at | formatDateTime }}</div>
                  </div>
                  <span class="v2-field__icon"
                        v-b-tooltip.hover
                        :title="$t('tasks.v2.download')"
                        @click="downloadMedia(exportFile)"
                        data-qid="reporting-export-file-download"
                  ><font-awesome-icon icon="cloud-download-alt"/></span>
                </div>
                <div v-else class="v2-field__empty task-file__empty">{{ $t('reports.no_export') }}</div>
                <DownloadAllAttachmentsModal v-if="canExportAttachments" ref="downloadAll" :object="activity" :hasExport="!!exportFile" hideButton></DownloadAllAttachmentsModal>
              </section>

              <div class="export-item export-item--separated">
                <div class="export-item__text">
                  <b>{{ $t('reports.export_tasks') }}</b>
                  <span class="text-muted">{{ $t('reports.export_tasks_hint') }}</span>
                </div>
                <span class="icon-btn"
                      @click="downloadTasks"
                      v-b-tooltip.hover
                      :title="$t('reports.export_tasks')"
                      data-qid="reporting-download-tasks"
                >
                  <font-awesome-icon icon="file-download"/>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT (60%) -->
        <div class="col-lg-7">

          <!-- DESCRIPTION (engagements show the project description here) -->
          <div class="panel" v-if="isEngagementProject" data-qid="reporting-description">
            <header class="panel__header">
              <div class="profile-compact">
                <div class="logo-container">
                  <font-awesome-icon icon="text" class="icon-svg--sm"/>
                </div>
                <div class="profile-compact__info">
                  <div class="profile-compact__top">
                    <div class="">
                      {{ $t('activities.description_label') }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="profile-compact__actions">
                <span class="icon-btn"
                      @click="showProjectDescriptionInput = true; projectDescription = activity.description"
                      data-qid="reporting-edit-description"
                      v-if="!showProjectDescriptionInput && canEditDescription"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span class="icon-btn btn-delete"
                      @click="showProjectDescriptionInput = false"
                      v-if="showProjectDescriptionInput"
                >
                  <font-awesome-icon icon="rotate-left"/>
                </span>
                <span class="icon-btn btn-edit"
                      @click="saveProjectDescription"
                      data-qid="reporting-save-description"
                      v-if="showProjectDescriptionInput"
                >
                  <font-awesome-icon icon="save"/>
                </span>
              </div>
            </header>

            <div class="panel__body">
              <RichTextEditor v-if="showProjectDescriptionInput"
                              v-model="projectDescription"
                              :rows="6"
                              :headings="false"
                              compact
                              :placeholder="$t('maintenance.third_parties.create.description_placeholder')"
              />
              <div v-else class="section-container">
                <RichTextView v-if="activity.description" :value="activity.description" :headings="false" compact class="text-formatted mb-0"/>
                <p v-else class="text-muted mb-0">{{ $t('system.missing_input') }}</p>
              </div>
            </div>
          </div>

          <!-- CONCLUSION -->
          <div class="panel">
            <header class="panel__header">
              <div class="profile-compact">
                <div class="logo-container">
                  <font-awesome-icon icon="text" class="icon-svg--sm"/>
                </div>
                <div class="profile-compact__info">
                  <div class="profile-compact__top">
                    <div class="">
                      {{ $t('table.summary') }}
                      <font-awesome-icon icon="file"
                                         v-b-tooltip.hover
                                         title=${project.conclusion} />
                    </div>
                  </div>
                </div>
              </div>

              <div class="profile-compact__actions">
              <span class="mr-3 btn btn-success float-right cursor-pointer"
                    @click="showObservations()"
                    v-if="activity.control_type === 'one time' && (activity.type === 'control_assessment' || activity.type === 'risk_assessment') && observationsOn"
                    qid="reporting-generate_report-button"
              >
                {{$t('projects.tab.observations')}}
              </span>
                <span class="icon-btn"
                      @click="showDescriptionInputFunction"
                      data-qid="activity-edit-description"
                      v-if="(!showDescriptionInput && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], activity) && activity.status != 'closed' && activity.status != 'completed') && ((activity.additional_client && getClient.slug != activity.additional_client.slug) || activity.additional_client == null)"
                >
                <font-awesome-icon icon="pen"/>
              </span>
                <span class="icon-btn btn-delete"
                      @click="cancelDescriptionForm"
                      data-qid="activity-edit-button-cancel"
                      v-if="showDescriptionInput"
                >
                        <font-awesome-icon icon="rotate-left"/>
                      </span>
                <span class="icon-btn btn-edit"
                      @click="formDescriptionSubmit"
                      data-qid="activity-edit-button-submit"
                      v-if="showDescriptionInput"
                >
                <font-awesome-icon icon="save"/>
              </span>
              </div>
            </header>

            <div class="panel__body">
              <div class="card" data-qid="basic-info-section">
                <div class="">
                  <div class="content-view-body">
                    <div>

                      <div v-if="showDescriptionInput">
                        <RichTextEditor
                            qid="edit-tp-form-description"
                            v-model="activityEditSummary"
                            :rows="6"
                            :headings="false"
                            compact
                            :placeholder="$t('table.summary')"
                        />
                      </div>

                      <div v-if="!showDescriptionInput" class="section-container">
                        <div v-if="activity.summary && activity.summary.length > descriptionLength">
                          <RichTextView v-if="showFullDescription" :value="activity.summary" :headings="false" compact class="text-formatted"/>
                          <p v-if="!showFullDescription" class="text-formatted"><RichTextView :value="activity.summary" :headings="false" compact :truncate="descriptionLength"/></p>
                          <div class="text-center mt-3" @click="showFullDescription=!showFullDescription">
                                  <span class="cursor-pointer" v-if="!showFullDescription">
                                    <span class="mr-2">
                                      {{ $t('system.view_more') }}
                                    </span>
                                    <font-awesome-icon icon="chevron-down"/>
                                  </span>
                            <span class="cursor-pointer" v-if="showFullDescription">
                                    <span class="mr-2">
                                      {{ $t('system.view_less') }}
                                    </span>
                                    <font-awesome-icon icon="chevron-up"/>
                                  </span>
                          </div>
                        </div>
                        <RichTextView v-if="activity.summary && activity.summary.length <= descriptionLength"
                                      :value="activity.summary"
                                      :headings="false"
                                      compact
                                      class="text-formatted"
                        />
                        <p v-if="!activity.summary" class="text-muted">
                          {{ $t('system.missing_input') }}
                        </p>
                      </div>
                      <br>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmRemoveAttachment
        :object="selectedFile"
        :show="showDeleteAttachmentModal"
        type="file"
        @closeConfirmModal="closeDeleteAttachmentModal"
    ></ConfirmRemoveAttachment>

    <ConfirmRemoveAttachment
        :object="selectedLink"
        :show="showDeleteLinkModal"
        type="link"
        @closeConfirmModal="closeDeleteLinkModal"
    ></ConfirmRemoveAttachment>


      <b-modal ref="observations" @hide="hideObservations" modal-class="task-modal" size="xl" hide-footer v-if="activity.control_type === 'one time' && (activity.type === 'control_assessment' || activity.type === 'risk_assessment' || activity.type === 'bia_assessment')">
        <template #modal-header="{ close }">
          <div class="" style="width: 100%;">
          <span @click="hideObservations" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
            <h5 class="mb-1 text-center" qid="update-user-title">
              {{ $t('projects.observations.title') }}
            </h5>
          </div>
        </template>
        <ObservationsTable :object="activity" @updated="updateActivity(false)" ></ObservationsTable>
      </b-modal>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import { openDocument as openDocumentInTab } from '@/util/openDocument.js';
import ObservationsTable from '@/views/Projects/ObservationsTable.vue';
import DownloadAllAttachmentsModal from '@/views/Projects/DownloadAllAttachmentsModal.vue';
import { isEngagement, observationsEnabled } from '@/views/Projects/Engagement/engagement.js';
import RichTextEditor from '@/components/richText/RichTextEditor.vue';
import RichTextView from '@/components/richText/RichTextView.vue';
export default {
  name: 'Reporting',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('projects.title'),
          to: '/projects'
        }
      ],
      activity: {},
      showFullDescription: false,
      descriptionLength: config.descriptionLength,
      edit: this.$route.params.edit,
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,
      showInput: false,

      itemToDelete: {},
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      inputLinkName: '',
      selectedFiles: [],
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      showDescriptionInput: false,
      activityEditSummary: '',

      // Tabs
      showActivityTab: false,
      showProcessTab: false,
      showControlTab: false,
      showSystemTab: false,
      showAssessmentTab: false,
      showThirdPartyTab: false,
      showScheduleTab: false,
      showHistoryTab: false,
      showObservationModal: false,
      buttonDisabled: false,
      tabIndex: 0,
      myGuides: [],
      open_tasks: {},
      closed_tasks: {},
      showStatusForm: false,
      options: [
        { name: this.$t('system.pending'), value: 'pending' },
        { name: this.$t('system.in_progress'), value: 'in progress' },
        { name: this.$t('system.completed'), value: 'completed' },
        { name: this.$t('system.closed'), value: 'closed' }
      ],
      selectedStatus: [],
      selectedTask: [],
      selectedAssignUsers: [],
      itemObject: {},
      reportError: '',
      allUsers: [],
      reportFiles: [],
      renamingFile: {},
      showProjectDescriptionInput: false,
      projectDescription: ''
    }
  },
  props: {
    object: {
      type: Object
    },
    measureTask: {
      type: Object
    }
  },
  computed: {
    // Engagements can switch Observations off in Settings
    observationsOn() {
      return observationsEnabled(this.activity);
    },
    getClient() {
      return this.$store.state.client
    },
    getFiles() {
      return this.selectedFiles
    },
    getUser() {
      return this.$store.state.user
    },
    getMedia() {
      let media = []

      media = _.filter(this.activity.media, function(mediaItem) {
        return mediaItem.collection_name == 'report';
      });

      return media
    },
    canExportAttachments() {
      return this.activity.type === 'control_assessment' || this.activity.type === 'risk_assessment';
    },
    isEngagementProject() {
      return isEngagement(this.activity);
    },
    canEditReport() {
      return this.isGranted([this.permissions.PROJECT_EDIT, this.permissions.PROJECT_EDIT_OWN], this.activity)
        && this.activity.status != 'closed' && this.activity.status != 'completed'
        && ((this.activity.additional_client && this.getClient.slug != this.activity.additional_client.slug) || this.activity.additional_client == null);
    },
    canEditDescription() {
      // The project description is a Manager field on engagements
      return this.canEditReport && !!(this.activity.engagement_permissions && this.activity.engagement_permissions.manage);
    },
    exportFile() {
      // The zip export is regenerated on request, so there is at most one
      return _.find(this.activity.media || [], mediaItem => mediaItem.collection_name === 'attachments') || null;
    },
    getLinks() {
      let links = []

      links = _.filter(this.activity.links, function(link) {
        return link.collection == 'report';
      });

      return links
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadContent(true);
      }
    },
    object: function (value) {
      this.activity = value
    }
  },
  components: {
    ConfirmRemoveAttachment,
    ObservationsTable,
    DownloadAllAttachmentsModal,
    RichTextEditor,
    RichTextView
  },
  methods: {
    goToList() {
    },
    showObservations() {
      this.$refs['observations'].show();
    },
    hideObservations() {
      // console.log('aaaaa');
      // this.$refs['observations'].hide();
      // this.$nextTick(() => {
      //   window.scrollTo({
      //     top: 0,
      //     behavior: 'smooth'
      //   });
      // });
    },
    resetFile() {
      let imagefile = document.querySelector('#file');
      imagefile.value = '';
    },
    loadContent(breadcrumbPush) {
      let self = this;
      piincHttp.get('projects/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.activity = response.data;

        if(self.activity.type === 'control_assessment' || self.activity.type === 'risk_assessment' || self.activity.type === 'bia_assessment'){
          self.breadcrumbItems.push({text: self.$t('projects.compliance_activity_table.assessment_title'), to: '/projects?view=list&family=assessments'});
        }else if(self.activity.type === 'update' || self.activity.type === 'validation'){
          self.breadcrumbItems.push({text: self.$t('projects.compliance_activity_table.asset_title'), to: '/projects?view=list&family=records'});
        }else if(self.activity.type === 'control' || self.activity.type === 'security_measure'){
          self.breadcrumbItems.push({text: self.$t('projects.compliance_activity_table.control_title'), to: '/projects?view=list&family=controls'});
        }

        if (breadcrumbPush) {
          // Push Third party name in breadcrumb array
          self.breadcrumbItems.push({text: self.activity.title, active: true});
        }



      }, function() {});
    },
    showViewTaskModal(object) {
      let self = this;
      this.error = '';

      this.selectedTask = object
      this.$refs['view-task-modal'].show()
    },
    hideViewTaskModal() {
      let self = this;
      this.error = '';

      this.selectedTask = {}
      this.$refs['view-task-modal'].hide()
    },
    updateActivity() {
      this.loadContent();
    },
    historyTabClicked() {
      this.showHistoryTab = true;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
    },
    basicTabClicked() {
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
    },
    activityTabClicked() {
      this.showActivityTab = true;
      this.showHistoryTab = false;
    },
    systemTabClicked() {
      this.showSystemTab = true;
      this.showHistoryTab = false;
    },
    assessmentTabClicked() {
      this.showAssessmentTab = true;
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
    },
    thirdPartyTabClicked() {
      this.showThirdPartyTab = true;
      this.showHistoryTab = false;
    },
    processTabClicked() {
      let self = this
      this.showHistoryTab = false;
      this.showProcessTab = true;
    },
    controlTabClicked() {
      this.showControlTab = true;
      this.showHistoryTab = false;
    },
    scheduleTabClicked() {
      this.showScheduleTab = true;
      this.showHistoryTab = false;
      this.showSystemTab = false;
      this.showThirdPartyTab = false;
      this.showProcessTab = false;
      this.showActivityTab = false;
    },
    handleCloseViewPModal () {
      this.loadPolicies();
      this.viewPolicyModal = false;
      this.selectedPolicy = []
    },
    showDescriptionInputFunction() {
      this.showDescriptionInput = true;
      this.activityEditSummary = this.activity.summary;
    },
    formDescriptionSubmit() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        summary: this.activityEditSummary
      }).then(function(response) {
        self.activity = response.data;
        self.showDescriptionInput = false;
        self.$toastr('success', self.$t('projects.update.success'))
      }, function(error) {
        if (error.status !== 200) {
          self.error = error.data.message;
        }
      })
    },
    generateReport(){
      this.successMessage = ''
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';
      piincHttp.get('projects/' + this.$route.params.slug + '/generate-report?lang='+language
      ).then(function(response) {
        self.disablePage = false;
        self.activity = response.data;
        if (response.status == 200) {
          self.successMessage = self.$t('reports.success_message');
        }
      }, function(error) {
        self.buttonDisabled = false;
        self.disablePage = false;
        if (error.data.message == 'Assessment template is deleted.') {
          self.reportError = self.$t('reports.assessment_error');
        }
      });
    },
    cancelDescriptionForm() {
      this.showDescriptionInput = false;
      this.activityEditSummary = this.activity.summary;
    },
    openViewPolicyModal(object) {
      this.selectedPolicy = object
      this.viewPolicyModal = true
    },
    reloadContent(object) {
      if (this.tabIndex == 1) {
        this.tabIndex = 0
      }
      // If slug is not changed, just reload content
      if (object.slug == this.activity.slug) {
        // Remove last item in breadcrumbs, because it will be added again on load content
        this.breadcrumbItems.pop();
        this.loadContent(true);
      } else {
        // If slug changed, reload state with new param
        this.$router.push({ name: 'projects.view', params: { slug: object.slug }})
      }
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-activity'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-activity'].hide()
    },
    confirmDelete(object) {
      let self = this;
      piincHttp.delete('projects/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('projects.delete.success'))
      }, function() {});
    },
    handleLink() {
      let self = this;
      let formData = new FormData();

      if (!this.inputLink.startsWith('http')) {
        this.inputLink = 'http://' + this.inputLink
      }

      formData.append("url", this.inputLink);
      formData.append("name", this.inputLinkName);
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);
      formData.append("collection", "report");

      piincHttp.post('links', formData).then(function() {
        // Remove last item in breadcrumbs, because it will be added again on load content
        self.breadcrumbItems.pop();
        self.$toastr('success', self.$t('maintenance.third_parties.link_add_success'))
        self.loadContent()
        self.inputLink = ''
        self.inputLinkName = ''
        self.showInput = false
      }, function() {})
    },
    openDocument(file) {
      // Images, PDFs and Office files open in a new browser tab
      openDocumentInTab(file);
    },
    // The project's tasks as an Excel file
    downloadTasks() {
      piincHttp.get('projects/' + this.activity.slug + '/tasks/download', { responseType: 'blob' }).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Project tasks.xlsx');
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    openDeleteAttachmentModal(object) {
      this.selectedFile = object
      this.showDeleteAttachmentModal = true
    },
    closeDeleteAttachmentModal () {
      this.selectedFile = {}
      this.showDeleteAttachmentModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    openDeleteLinkModal(object) {
      this.selectedLink = object
      this.showDeleteLinkModal = true
    },
    closeDeleteLinkModal () {
      this.selectedLink = {}
      this.showDeleteLinkModal = false;
      // Remove last item in breadcrumbs, because it will be added again on load content
      this.breadcrumbItems.pop();
      this.loadContent();
    },
    uploadReportFiles() {
      let self = this;
      if (!this.reportFiles.length) {
        return;
      }

      let formData = new FormData();
      for (let i = 0; i < this.reportFiles.length; i++) {
        if (this.reportFiles[i].size > 10485760) {
          this.error = this.$t('system.file_size');
          this.reportFiles = [];
          return;
        }
        formData.append('document[]', this.reportFiles[i].file);
      }

      formData.append("name", "documents");
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);
      formData.append("collection", "report");

      piincHttp.post('documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function() {
        self.reportFiles = [];
        self.breadcrumbItems.pop();
        self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'))
        self.error = ''
        self.loadContent()
      }, function(error) {
        self.reportFiles = [];
        self.error = error.data && error.data.message ? error.data.message : '';
        if (error.data && error.data.errors && error.data.errors['document.0']) {
          self.error += ' ' + error.data.errors['document.0'][0]
        }
      })
    },
    // Files dropped on the attachments go through the same upload as the Add attachment button
    dropReportFiles(event) {
      if (!this.canEditReport || !event.dataTransfer || !event.dataTransfer.files.length) {
        return;
      }
      this.$refs['report-uploader'].add(event.dataTransfer.files);
    },
    startRename(file) {
      const name = file.file_name || '';
      const dot = name.lastIndexOf('.');
      this.renamingFile = { id: file.id, name: dot > 0 ? name.substring(0, dot) : name };
    },
    cancelRename() {
      this.renamingFile = {};
    },
    saveFileName() {
      let self = this;
      if (!this.renamingFile.name || !this.renamingFile.name.trim()) {
        return;
      }

      piincHttp.put('documents/' + this.renamingFile.id, {
        file_name: this.renamingFile.name.trim()
      }).then(function() {
        self.renamingFile = {};
        self.breadcrumbItems.pop();
        self.$toastr('success', self.$t('reports.rename_success'));
        self.loadContent();
      }, function(error) {
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    saveProjectDescription() {
      let self = this;
      piincHttp.put('projects/' + this.$route.params.slug, {
        description: this.projectDescription
      }).then(function(response) {
        self.activity = response.data;
        self.showProjectDescriptionInput = false;
        self.$toastr('success', self.$t('projects.update.success'));
      }, function(error) {
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    },
    onFileChange(file) {
      let files = file.target.files;
      let self = this;
      let formData = new FormData();
      let sizeError = false;

      for (var i = 0; i < files.length; i++) {
        // Equal to 10 MB
        if (files[i].size > 10485760) {
          sizeError = true;
        }
        formData.append('document[]', files[i]);
      }

      formData.append("name", "documents");
      formData.append("model_type", this.activity.who_am_i);
      formData.append("model_id", this.activity.id);
      formData.append("collection", "report");

      if (sizeError) {
        this.error = this.$t('system.file_size')
      } else {
        piincHttp.post('documents', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function() {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'))
          self.error = ''
          self.loadContent()
        }, function(error) {
          self.error = error.data.message;
          if (error.data.errors['document.0']) {
            self.error += ' ' + error.data.errors['document.0'][0]
          }
        })
      }
    },
    loadGuides() {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 100,
          language: 'en',
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides = response.data.items
        }
      }, function() {});
    },
    showFormFunction() {
      let self = this
      this.selectedStatus = []
      if (this.activity.status) {
        this.selectedStatus = _.find(this.options, function(item) {
          return item.value === self.activity.status;
        });
      }
      this.showStatusForm = true
    },
  },
  created () {
    this.activity = this.object;
    if (this.getClient && this.getClient.slug) {
      this.loadContent(true);
    }

  }
}
</script>
